import React, { useState, useEffect } from 'react';

import { getApi } from '../../../api';
import { SureYesNoButton, SureYesNoButton2 } from '../../../ClickOnceButton';
import { useAuth } from '../../../context/AuthContext';
import { useDevices } from '../../../context/DeviceContext';
import { Fetch } from '../../../Fetch';
import { smallTextStyle } from '../../../styles/constants';
import type { Device } from '../../../types';
import { ACTIVATE_DEACTIVATE_TIMEOUT_S } from '../../../utils/constants';
import { formatAge } from '../../../utils/dateUtils';
import { getMaxTraceTimeS } from '../../../utils/limits';
import logger from '../../../utils/logger';

/**
 * Statistics calculated from filtered devices
 */
interface DeviceStats {
  count: number;
  lastSignalAge: number | null;
  checkedCount: number;
  uncheckedCount: number;
  checkpointCount: number;
  currentCheckpointName: string | null;
  traceCount: number;
}

/**
 * Display strings based on user permissions
 */
interface DisplayStrings {
  lastUpdate: string;
  checker: string;
  checkpoint: string;
}

/**
 * Props for the InfoBox component
 */
export interface InfoBoxProps {
  /** All devices from the system */
  devices?: Device[];
  /** Devices after applying filters */
  filteredDevices?: Device[];
  /** Current checkpoint device ID */
  currentCheckpoint?: string | null;
  /** Last successful data update time */
  lastUpdateTime?: Date | null;
}

/**
 * InfoBox Component - Device statistics and batch operations
 *
 * Displays device statistics and provides batch operations for tracking devices.
 * Shows different information based on user permissions (admin, checker).
 * Includes "Track All" and "Deactivate All" functionality for device management.
 *
 * @example
 * <InfoBox
 *   devices={allDevices}
 *   filteredDevices={visibleDevices}
 *   currentCheckpoint={currentCheckpointId}
 *   lastUpdateTime={lastUpdate}
 * />
 */
export const InfoBox: React.FC<InfoBoxProps> = ({
  devices = [],
  filteredDevices = [],
  currentCheckpoint = null,
  lastUpdateTime = null,
}) => {
  const { token, isAdmin, isChecker } = useAuth();
  const { showBatchTrackingButtons } = useDevices();

  // Batch operation states
  const [activateAll, setActivateAll] = useState(false);
  const [deactivateAll, setDeactivateAll] = useState(false);

  // Auto-timeout for batch operations (UX safety feature)
  useEffect(() => {
    if (!activateAll) return;

    const timer = setTimeout(() => {
      setActivateAll(false);
    }, ACTIVATE_DEACTIVATE_TIMEOUT_S * 1000);

    return () => clearTimeout(timer);
  }, [activateAll]);

  useEffect(() => {
    if (!deactivateAll) return;

    const timer = setTimeout(() => {
      setDeactivateAll(false);
    }, ACTIVATE_DEACTIVATE_TIMEOUT_S * 1000);

    return () => clearTimeout(timer);
  }, [deactivateAll]);

  /**
   * Calculate device statistics from filtered devices
   */
  const calculateStats = (): DeviceStats => {
    if (!filteredDevices.length) {
      return {
        count: 0,
        lastSignalAge: null,
        checkedCount: 0,
        uncheckedCount: 0,
        checkpointCount: 0,
        currentCheckpointName: null,
        traceCount: 0,
      };
    }

    const now = Date.now() / 1000;
    let minSignalAge = Number.MAX_SAFE_INTEGER;
    let checkedCount = 0;
    let uncheckedCount = 0;
    let checkpointCount = 0;
    let currentCheckpointName: string | null = null;
    let traceCount = 0;

    filteredDevices.forEach((device) => {
      // Calculate signal age
      if (device.timestamp) {
        const signalAge = now - new Date(device.timestamp).getTime() / 1000;
        if (signalAge < minSignalAge) {
          minSignalAge = signalAge;
        }
      }

      // Count device types
      if (device.checkpoint) {
        checkpointCount++;
      } else if (device.checkinTime) {
        checkedCount++;
      } else {
        uncheckedCount++;
      }

      // Find current checkpoint name
      if (currentCheckpoint === device.id) {
        currentCheckpointName = device.title || device.text || null;
      }

      // Count trace-eligible devices
      if (device.traceTime) {
        const traceAge = now - new Date(device.traceTime).getTime() / 1000;
        if (traceAge >= 0 && traceAge <= getMaxTraceTimeS()) {
          traceCount++;
        }
      }
    });

    return {
      count: filteredDevices.length,
      lastSignalAge: minSignalAge !== Number.MAX_SAFE_INTEGER ? minSignalAge : null,
      checkedCount,
      uncheckedCount,
      checkpointCount,
      currentCheckpointName,
      traceCount,
    };
  };

  const stats = calculateStats();

  /**
   * Handle activate all devices operation
   */
  const handleActivateAll = (): void => {
    setActivateAll(true);
    setDeactivateAll(false);
  };

  /**
   * Handle deactivate all devices operation
   */
  const handleDeactivateAll = (): void => {
    setDeactivateAll(true);
    setActivateAll(false);
  };

  /**
   * Generate display strings based on user permissions and stats
   */
  const getDisplayStrings = (): DisplayStrings => {
    const strings: DisplayStrings = {
      lastUpdate: '',
      checker: '',
      checkpoint: '',
    };

    // Last signal info (admin only)
    if (isAdmin && stats.lastSignalAge !== null) {
      strings.lastUpdate = `Last Signal: ${formatAge(stats.lastSignalAge + 15)}`;
    }

    // Checker info
    if (isChecker) {
      if (stats.checkedCount > 0) {
        strings.checker = `Checked: ${stats.checkedCount} Unchecked: ${stats.uncheckedCount}`;
      }

      if (stats.currentCheckpointName) {
        strings.checkpoint = `CURRENT CHECKPOINT: ${stats.currentCheckpointName}`;
      } else if (stats.checkedCount > 0) {
        strings.checkpoint = `${stats.checkpointCount} CHECKPOINTS`;
      }
    }

    return strings;
  };

  const displayStrings = getDisplayStrings();

  // Get non-checkpoint devices for batch operations
  const nonCheckpointDevices = devices.filter((device) => !device.checkpoint);

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: '2vmin',
    left: '10vmin',
    textAlign: 'left',
    ...smallTextStyle,
    color: 'black',
    zIndex: 1000,
  };

  const statusTextStyle: React.CSSProperties = {
    fontWeight: 600,
  };

  return (
    <div>
      {/* Device statistics and controls */}
      <div style={containerStyle}>
        {/* Last update info */}
        {displayStrings.lastUpdate && (
          <>
            {displayStrings.lastUpdate}
            <br />
          </>
        )}

        {/* Checker statistics */}
        {isChecker && displayStrings.checker && (
          <small style={statusTextStyle}>
            {displayStrings.checker}
            <br />
          </small>
        )}

        {/* Checkpoint info */}
        {isChecker && displayStrings.checkpoint && (
          <small>
            {displayStrings.checkpoint}
            <br />
          </small>
        )}

        {/* Batch operation buttons */}
        {showBatchTrackingButtons && !activateAll && (
          <SureYesNoButton2
            text="Track All Devices"
            info="It might take several minutes to activate tracing for all connected devices."
            onClick={handleActivateAll}
          />
        )}

        {showBatchTrackingButtons && !deactivateAll && (
          <SureYesNoButton
            text="Deactivate Track Devices"
            info="It might take several minutes to deactivate tracing for all connected devices."
            onClick={handleDeactivateAll}
          />
        )}

        {/* Operation status indicators */}
        {activateAll && (
          <small>
            <b>TRACK ALL DEVICES PENDING</b>
            <br />
          </small>
        )}

        {deactivateAll && (
          <small>
            <b>DEACTIVATE TRACK ALL DEVICES PENDING</b>
            <br />
          </small>
        )}
      </div>

      {/* Batch operation API calls */}
      <div>
        {/* Activate tracing for all non-checkpoint devices */}
        {activateAll &&
          nonCheckpointDevices.map((device) => (
            <Fetch
              key={`fetch-trace-${device.id}`}
              submit={true}
              method="POST"
              body={{ device: device.id, port: 2, data: '9100000001' }}
              onError={(e: Error | { error: string }) => {
                logger.debug('Error activating trace for device', device.id, e);
              }}
              onResult={() => {
                logger.debug('Activated trace for device', device.id);
              }}
              api={`${getApi()}/downlink`}
            />
          ))}

        {/* Deactivate tracing for all non-checkpoint devices */}
        {deactivateAll &&
          nonCheckpointDevices.map((device) => (
            <Fetch
              key={`fetch-deactivate-trace-${device.id}`}
              submit={true}
              method="POST"
              body={{ device: device.id, port: 2, data: '9100000000' }}
              onError={(e: Error | { error: string }) => {
                logger.debug('Error deactivating trace for device', device.id, e);
              }}
              onResult={() => {
                logger.debug('Deactivated trace for device', device.id);
              }}
              api={`${getApi()}/downlink`}
            />
          ))}
      </div>
    </div>
  );
};

export default InfoBox;
